import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CheckMark, Close } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Button, Loader, Popover, Stack, Typography } from '@lyearn/ui';
import { Image } from '@/components/Image';
import { VideoSlideTemplateCategoryEnum } from '@/types/schema';

import {
  useVideoSlideTemplatesQuery,
  VideoSlideTemplatesQuery,
} from '../graphql/queries/generated/videoSlideTemplates';

export type VideoSlideTemplate = NonNullable<
  NonNullable<VideoSlideTemplatesQuery['videoSlideTemplates']['edges']>[number]
>['node'];

interface VideoTemplatePickerProps {
  selectedTemplateId: string;
  onChangeTemplate: (slideTemplateId: string, template: VideoSlideTemplate) => void;
  disabled?: boolean;
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  category?: VideoSlideTemplateCategoryEnum;
}

export function VideoTemplatePicker(props: VideoTemplatePickerProps) {
  const { selectedTemplateId, onChangeTemplate, disabled, children, onOpenChange, category } =
    props;
  const { t } = useTranslation('Course');
  const [isOpen, setIsOpen] = React.useState(false);
  const [localSelectedId, setLocalSelectedId] = React.useState<string>(selectedTemplateId);

  const [{ data, fetching }] = useVideoSlideTemplatesQuery({
    pause: !isOpen || disabled,
    variables: {
      first: 24,
      filters: {
        categories: [category ?? VideoSlideTemplateCategoryEnum.Default],
      },
    },
  });

  const templates = React.useMemo<VideoSlideTemplate[]>(
    () => data?.videoSlideTemplates?.edges?.map((edge) => edge.node) ?? [],
    [data],
  );

  const handleOpenChange = React.useCallback(
    (nextOpen?: boolean) => {
      if (disabled) return;
      if (nextOpen) setLocalSelectedId(selectedTemplateId);
      const next = Boolean(nextOpen);
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [disabled, onOpenChange, selectedTemplateId],
  );

  const handleClose = React.useCallback(() => setIsOpen(false), []);

  const handleConfirm = React.useCallback(() => {
    const selected = templates.find((tmpl) => tmpl.id === localSelectedId);
    if (selected?.id) {
      onChangeTemplate(selected.id, selected);
    }
    setIsOpen(false);
  }, [localSelectedId, onChangeTemplate, templates]);

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Button>{children}</Popover.Button>
      <Popover.Content
        className="flex h-[52rem] w-[52rem] flex-col overflow-hidden rounded-20 bg-bg-primary p-0 shadow-lg"
        position="right-start"
        renderAs="popover">
        {/* Header */}
        <Stack
          alignItems="center"
          className="shrink-0 px-20 pt-20 pb-16"
          direction="row"
          justifyContent="between">
          <Typography color="text-text-primary" variant="heading-s">
            Choose Slide Layout
          </Typography>
          <Stack
            alignItems="center"
            aria-label={t('Common:Actions.Close')}
            className="cursor-pointer rounded-[1.6rem] bg-bg-primary p-12"
            justifyContent="center"
            onClick={handleClose}>
            <Close className="h-24 w-24 text-text-primary" />
          </Stack>
        </Stack>

        {/* Content */}
        <div className="min-h-0 flex-1 overflow-y-auto px-20 pb-4">
          {fetching ? (
            <Stack alignItems="center" className="h-full" justifyContent="center">
              <Loader size={20} />
            </Stack>
          ) : templates.length === 0 ? (
            <Typography color="text-text-placeholder" variant="body-short-01">
              {t('Common:NoResultsFound')}
            </Typography>
          ) : (
            <div className="grid grid-cols-2 gap-16">
              {templates.map((template) => {
                const isSelected = template.id === localSelectedId;

                return (
                  <Stack
                    key={template.id}
                    className={cn(
                      'relative cursor-pointer overflow-hidden rounded-16 border-2 transition-colors',
                      isSelected ? 'border-[#020202]' : 'border-[#EAEAEA] hover:border-[#C0C0C0]',
                    )}
                    direction="col"
                    onClick={() => setLocalSelectedId(template.id)}>
                    {/* Preview image */}
                    <Stack
                      className="w-full overflow-hidden bg-bg-secondary"
                      style={{ aspectRatio: '16/9' }}>
                      <Image
                        alt={template.name}
                        className="h-full w-full object-contain"
                        height={126}
                        src={template.preview_url ?? undefined}
                        width={224}
                      />
                    </Stack>

                    {/* Selected checkmark badge */}
                    {isSelected ? (
                      <span className="absolute right-10 top-10 flex h-28 w-28 shrink-0 items-center justify-center rounded-full bg-[#020202]">
                        <CheckMark className="h-14 w-14 text-white" />
                      </span>
                    ) : null}
                  </Stack>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer — always visible */}
        <Stack
          alignItems="center"
          className="shrink-0 border-t border-border px-20 py-12"
          direction="row"
          gap={12}
          justifyContent="end">
          <Button shape="pill" size="medium" variant="outlined" onClick={handleClose}>
            {t('Common:Actions.Close')}
          </Button>
          <Button
            disabled={!localSelectedId || localSelectedId === selectedTemplateId}
            shape="pill"
            size="medium"
            variant="filled"
            onClick={handleConfirm}>
            Apply
          </Button>
        </Stack>
      </Popover.Content>
    </Popover>
  );
}
